import Branch16 from '@carbon/icons-react/es/branch/16';
import Close20 from '@carbon/icons-react/es/close/20';
import { Col, FormInstance, Row as AntRow, Tooltip } from 'antd';
import { FormListFieldData, FormListOperation } from 'antd/es/form/FormList';
import React, { Dispatch, SetStateAction } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  SortableContainer,
  SortableElement,
  SortEndHandler,
} from 'react-sortable-hoc';
import styled, { css } from 'styled-components';
import { CreateBranchingDetailsProps } from 'api/form/types';
import { Button } from 'components/atoms/button';
import { DragHandle } from 'components/atoms/drag-handle';
import { Form } from 'components/atoms/form';
import { TextArea } from 'components/atoms/input';
import { Color } from 'components/colors';
import { FormItemWrapper } from 'components/molecules/forms/form-fields/form-elements';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import {
  useDeleteFieldQuestionMutation,
  useDeleteOptionMutation,
  useUpdateFieldOptionMutation,
  useUpdateFieldQuestionMutation,
} from 'queries/form-template';
import { MaxBranchingDepth } from '../../constants';
import { Values } from '../form-field-configuration';
import { ColorDropdown, getArbitraryColor } from './color-dropdown';

const StyledTextArea = styled(TextArea)`
  width: 15rem;

  .ant-form-item-has-error &:focus {
    outline: solid 2px;
    outline-color: var(--error-color);
  }
`;

const Row = styled(AntRow)`
  z-index: var(--zindex-popover-low);
`;

const FormItem = styled(Form.Item)`
  && {
    margin-bottom: var(--layout-01);
  }
`;

const Circle = styled.span<{ color: Color }>`
  --tag-border-color: var(--tag-surface-color);
  height: 24px;
  width: 24px;
  border-radius: 50%;
  ${({ color }) => css`
    --tag-surface-color: var(--ozone-${color}-surface);
    --tag-color: var(--ozone-${color});
  `};
  border: 1px solid var(--tag-border-color);
  background: var(--tag-surface-color);
  color: var(--tag-color);

  display: flex;
  align-items: center;
  justify-content: center;
`;

type ListOperation = Pick<FormListOperation, 'remove'>;

interface ItemProps extends ListOperation {
  formField: FormListFieldData;
  form: FormInstance<Values>;
  isOptionColorSupported: boolean;
  templateId: number;
  questionId: number;
  questionDepth: number;
  showBranch?: boolean;
  setCreateBranchingDetails?: Dispatch<
    SetStateAction<null | CreateBranchingDetailsProps>
  >;
  hasAssociatedField: boolean;
}

const generateChar = (seed = '') => {
  return seed.length > 0 ? seed[0] : 'A';
};

// sortableItem for row questions
const SortableRowItem = SortableElement<ItemProps>(
  ({
    formField,
    form,
    remove,
    isOptionColorSupported,
    templateId,
    questionId,
    hasAssociatedField,
  }: ItemProps) => {
    const requiredMessage = useTranslation('input.required');
    const uniqueQuestionErrorMessage = useTranslation(
      'duplicate.questions.error',
    );
    const currentOptionFieldPath = ['rows', formField.name];
    const currentOption = form.getFieldValue(currentOptionFieldPath);
    const translate = useTranslate();
    const { rows } = form.getFieldsValue(['rows']);
    const rowLabels = rows.reduce((acc, curr) => {
      if (curr.questionId !== currentOption.questionId) {
        return [...acc, curr.questionText];
      }

      return acc;
    }, []);

    const { mutate: updateOptionMutation } =
      useUpdateFieldOptionMutation(templateId);

    const { mutateAsync: deleteQuestionMutation } =
      useDeleteFieldQuestionMutation({
        templateId,
        questionText: currentOption?.questionText,
      });

    const { mutate: updateFormQuestionMutation } =
      useUpdateFieldQuestionMutation(templateId);

    const optionDeleteMessage = 'form_templates.delete_option_message';
    const deleteOptionConfirmation = useDeleteConfirmModal({
      title: translate('form_templates.delete_option'),
      content: translate(optionDeleteMessage, {
        optionLabel: currentOption?.questionText,
        strong: (chunks) => <strong>{chunks}</strong>,
      }),
      onOk: () =>
        deleteQuestionMutation(
          { questionId: currentOption?.questionId },
          {
            onSuccess: () => remove(formField.name),
          },
        ),
    });

    return currentOption ? (
      <Row gutter={8}>
        {!hasAssociatedField && (
          <Col flex="40px">
            <DragHandle size="small" />
          </Col>
        )}
        {!hasAssociatedField && isOptionColorSupported ? (
          <Col flex="40px">
            <FormItem
              {...formField}
              name={[formField.name, 'color']}
              fieldKey={[formField.fieldKey, 'color']}
              rules={[
                {
                  required: true,
                  message: requiredMessage,
                },
              ]}
              initialValue={getArbitraryColor(formField.name)}
            >
              <ColorDropdown
                onChange={(color) => {
                  updateOptionMutation({
                    questionId,
                    optionId: currentOption.questionId,
                    payload: {
                      color,
                    },
                  });
                }}
                IconComponent={({ color }) => (
                  <Circle color={color}>
                    {generateChar(currentOption?.questionText)}
                  </Circle>
                )}
              />
            </FormItem>
          </Col>
        ) : null}
        <Col
          flex={
            hasAssociatedField
              ? '290px'
              : isOptionColorSupported
              ? '170px'
              : '210px'
          }
        >
          <FormItemWrapper>
            <Form.Item
              name={[formField.name, 'questionText']}
              fieldKey={[formField.fieldKey, 'questionText']}
              rules={[
                {
                  required: true,
                  message: requiredMessage,
                },
                () => ({
                  validator(_, value) {
                    if (!rowLabels.includes(value.trim())) {
                      return Promise.resolve();
                    }

                    return Promise.reject(
                      new Error(uniqueQuestionErrorMessage),
                    );
                  },
                }),
              ]}
            >
              <StyledTextArea
                autoSize={{ minRows: 1, maxRows: 3 }}
                onPressEnter={(event) => {
                  event.preventDefault();
                }}
                disabled={hasAssociatedField}
                onBlur={() => {
                  if (currentOption.questionText) {
                    if (!rowLabels.includes(currentOption.questionText)) {
                      updateFormQuestionMutation({
                        questionId: currentOption.questionId,
                        payload: {
                          questionText: currentOption.questionText,
                          description: currentOption.descirption,
                          required: currentOption.required,
                        },
                      });
                    }
                  }
                }}
                defaultValue={currentOption.questionText}
              />
            </Form.Item>
          </FormItemWrapper>
        </Col>

        {!hasAssociatedField && (
          <Col flex="40px">
            <Form.Item shouldUpdate>
              <Tooltip
                mouseEnterDelay={1}
                title={<FormattedMessage id="settings.fields.remove_option" />}
              >
                <Button
                  size="small"
                  type="text"
                  icon={<Close20 />}
                  onClick={() => {
                    deleteOptionConfirmation();
                  }}
                />
              </Tooltip>
            </Form.Item>
          </Col>
        )}
      </Row>
    ) : null;
  },
);

const SortableItem = SortableElement<ItemProps>(
  ({
    formField,
    form,
    remove,
    isOptionColorSupported,
    templateId,
    questionId,
    questionDepth,
    setCreateBranchingDetails,
    hasAssociatedField,
    showBranch = true,
  }: ItemProps) => {
    const requiredMessage = useTranslation('input.required');
    const uniqueOptionErrorMessage = useTranslation('duplicate.options.error');
    const currentOptionFieldPath = ['options', formField.name];
    const currentOption = form.getFieldValue(currentOptionFieldPath);
    const { options } = form.getFieldsValue(['options']);
    const optionLabels = options.reduce((acc, curr) => {
      if (curr.optionId !== currentOption.optionId) {
        return [...acc, curr.optionLabel];
      }

      return acc;
    }, []);

    const translate = useTranslate();

    const { mutate: updateOptionMutation } =
      useUpdateFieldOptionMutation(templateId);

    const { mutateAsync: deleteOptionMutation } = useDeleteOptionMutation({
      templateId,
      optionLabel: currentOption?.optionLabel,
    });
    const optionDeleteMessage = (currentOption?.branches || []).length
      ? 'form_templates.delete_option_message_with_branch'
      : 'form_templates.delete_option_message';
    const deleteOptionConfirmation = useDeleteConfirmModal({
      title: translate('form_templates.delete_option'),
      content: translate(optionDeleteMessage, {
        optionLabel: currentOption?.optionLabel,
        strong: (chunks) => <strong>{chunks}</strong>,
      }),
      onOk: () =>
        deleteOptionMutation(
          { optionId: currentOption.optionId },
          {
            onSuccess: () => remove(formField.name),
          },
        ),
    });

    const TextAreaComponent = showBranch ? TextArea : StyledTextArea;

    return currentOption ? (
      <Row gutter={8}>
        {!hasAssociatedField && (
          <Col flex="40px">
            <DragHandle size="small" />
          </Col>
        )}
        {!hasAssociatedField && isOptionColorSupported ? (
          <Col flex="40px">
            <FormItem
              {...formField}
              name={[formField.name, 'color']}
              fieldKey={[formField.fieldKey, 'color']}
              rules={[
                {
                  required: true,
                  message: requiredMessage,
                },
              ]}
              initialValue={getArbitraryColor(formField.name)}
            >
              <ColorDropdown
                onChange={(color) => {
                  updateOptionMutation({
                    questionId,
                    optionId: currentOption.optionId,
                    payload: {
                      color,
                    },
                  });
                }}
                IconComponent={({ color }) => (
                  <Circle color={color}>
                    {generateChar(currentOption?.optionLabel)}
                  </Circle>
                )}
              />
            </FormItem>
          </Col>
        ) : null}
        <Col
          flex={
            hasAssociatedField
              ? '290px'
              : isOptionColorSupported
              ? '170px'
              : '210px'
          }
        >
          <FormItemWrapper>
            <Form.Item
              {...formField}
              name={[formField.name, 'optionLabel']}
              fieldKey={[formField.fieldKey, 'optionLabel']}
              rules={[
                {
                  required: true,
                  message: requiredMessage,
                },
                () => ({
                  validator(_, value) {
                    if (!optionLabels.includes(value.trim())) {
                      return Promise.resolve();
                    }

                    return Promise.reject(new Error(uniqueOptionErrorMessage));
                  },
                }),
              ]}
            >
              <TextAreaComponent
                autoSize={{ minRows: 1, maxRows: 3 }}
                onPressEnter={(event) => {
                  event.preventDefault();
                }}
                disabled={hasAssociatedField}
                onBlur={() => {
                  if (currentOption.optionLabel) {
                    if (!optionLabels.includes(currentOption.optionLabel)) {
                      updateOptionMutation({
                        questionId,
                        optionId: currentOption.optionId,
                        payload: {
                          optionLabel: currentOption.optionLabel,
                        },
                      });
                    }
                  }
                }}
              />
            </Form.Item>
          </FormItemWrapper>
        </Col>
        {showBranch && (
          <Col flex="40px">
            <Tooltip
              mouseEnterDelay={1}
              placement="left"
              title={
                currentOption.branches?.length ? (
                  <FormattedMessage id="form_templates.branch_exists" />
                ) : questionDepth >= MaxBranchingDepth ? (
                  <FormattedMessage
                    id="form_templates.max_depth_limit_reached"
                    values={{
                      count: MaxBranchingDepth,
                    }}
                  />
                ) : (
                  <FormattedMessage id="form_templates.add_branch" />
                )
              }
            >
              <Button
                size="small"
                type="text"
                icon={<Branch16 />}
                onClick={() =>
                  setCreateBranchingDetails &&
                  setCreateBranchingDetails({
                    questionId,
                    optionId: currentOption.optionId,
                  })
                }
                disabled={
                  currentOption.branches?.length ||
                  questionDepth >= MaxBranchingDepth
                }
              />
            </Tooltip>
          </Col>
        )}

        {!hasAssociatedField && (
          <Col flex="40px">
            <Form.Item shouldUpdate>
              <Tooltip
                mouseEnterDelay={1}
                title={<FormattedMessage id="settings.fields.remove_option" />}
              >
                <Button
                  size="small"
                  type="text"
                  icon={<Close20 />}
                  onClick={() => {
                    deleteOptionConfirmation();
                  }}
                />
              </Tooltip>
            </Form.Item>
          </Col>
        )}
      </Row>
    ) : null;
  },
);

interface SortableOptionsProps extends ListOperation {
  fields: FormListFieldData[];
  form: FormInstance<Values>;
  onSortEnd: SortEndHandler;
  isOptionColorSupported: boolean;
  templateId: number;
  questionId: number;
  questionDepth: number;
  setCreateBranchingDetails?: Dispatch<
    SetStateAction<null | CreateBranchingDetailsProps>
  >;
  hasAssociatedField: boolean;
  sortableOptionType?: string;
  showBranch?: boolean;
}

export const SortableOptions = SortableContainer<SortableOptionsProps>(
  ({
    fields,
    form,
    remove,
    isOptionColorSupported,
    templateId,
    questionId,
    questionDepth,
    setCreateBranchingDetails,
    hasAssociatedField,
    sortableOptionType = 'option',
    showBranch = true,
  }: SortableOptionsProps) => {
    return (
      <div>
        {sortableOptionType === 'question'
          ? fields.map((formField) => {
              return (
                <SortableRowItem
                  key={`formField-${formField.name}`}
                  index={formField.name}
                  formField={formField}
                  form={form}
                  remove={remove}
                  isOptionColorSupported={isOptionColorSupported}
                  templateId={templateId}
                  questionId={questionId}
                  questionDepth={questionDepth}
                  hasAssociatedField={hasAssociatedField}
                />
              );
            })
          : fields.map((formField) => {
              return (
                <SortableItem
                  key={`formField-${formField.name}`}
                  index={formField.name}
                  formField={formField}
                  form={form}
                  remove={remove}
                  isOptionColorSupported={isOptionColorSupported}
                  templateId={templateId}
                  questionId={questionId}
                  questionDepth={questionDepth}
                  setCreateBranchingDetails={setCreateBranchingDetails}
                  hasAssociatedField={hasAssociatedField}
                  showBranch={showBranch}
                />
              );
            })}
      </div>
    );
  },
);
